// src/Components/Ui/SafeImg.tsx
// <img> that never shows the browser's broken-image glyph. When the source
// fails to load it either swaps to `fallbackSrc`, renders `fallback`, or hides.
import React, { useEffect, useState } from "react";

type SafeImgProps = React.ImgHTMLAttributes<HTMLImageElement> & {
  fallbackSrc?: string;
  /** Rendered in place of the image when it fails and no fallbackSrc works. */
  fallback?: React.ReactNode;
};

const SafeImg: React.FC<SafeImgProps> = ({
  src,
  fallbackSrc,
  fallback,
  onError,
  alt = "",
  ...rest
}) => {
  const [failed, setFailed] = useState(false);
  const [useFallbackSrc, setUseFallbackSrc] = useState(false);
  const [retried, setRetried] = useState(false);

  useEffect(() => {
    setFailed(false);
    setUseFallbackSrc(false);
    setRetried(false);
  }, [src]);

  if (!src || failed) {
    return fallback ? <>{fallback}</> : null;
  }

  // One retry with a cache-busting query survives a transient CDN 5xx.
  const base = useFallbackSrc && fallbackSrc ? fallbackSrc : src;
  const effectiveSrc = retried ? `${base}${base.includes("?") ? "&" : "?"}r=${Date.now() % 100000}` : base;

  return (
    <img
      {...rest}
      alt={alt}
      src={effectiveSrc}
      onError={(e) => {
        onError?.(e);
        if (!retried) {
          setRetried(true);
        } else if (fallbackSrc && !useFallbackSrc) {
          setRetried(false);
          setUseFallbackSrc(true);
        } else {
          setFailed(true);
        }
      }}
    />
  );
};

export default SafeImg;
